import { SpinnerService } from './../../../common/spinner/spinner.service';
import { CurrencyPipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { ChartColors } from 'src/app/common/helper-functions';
import { iGroup, IGroupTags, iIgnoreTags, iTrack } from '../common/interface';
import { ExpenseTrackerStoreState } from '../store/reducers';
import { Store } from 'src/app/common/state/store';
import { getAllIgnoreTags } from '../store/reducers/ignore-tags/ignore-tags.selector';
import {
  getAllGroups,
  getGroupById,
} from '../store/reducers/group/group.selectors';
import { getAllGroupTags } from '../store/reducers/group-tags/group-tags.selector';
import { Subject } from 'rxjs';
import { takeUntil, distinctUntilChanged, take, map } from 'rxjs/operators';
import { UpdateGroupAction } from '../store/actions/group.action';

//#region interfaces

export interface Expense {
  id: number;
  uuid: string;
  userId: number;
  userGroupId: number;
  trackerTypeId: number;
  trackerValue: number;
  dated: string;
  datedObj?: Date;
  day: number;
  month: number;
  year: number;
  groupId?: number;
  isReoccurring?: number;
  reoccurringInterval?: number;
  reoccurringDay?: number;
  tags: string;
  tagsArray: string[];
  icon?: string;
  extraInfo?: ExpenseExtraInfo;
  createdBy: number;
  createdAt: Date;
  updatedBy: number;
  updatedAt: Date;
  isDeleted: number;
  deletedBy: number;
  deletedAt: Date;
  versionNo: number;
  iconUrl?: string;
  receiptFileName?: string;
  reOccurringId: number;
  splitTags: SplitTags[];

  weight?: number;
  quantity?: number;
  rate?: number;
}

export interface TopExpenseByDay {
  day: number;
  total: number;
  expenseIds: Set<number>;
  expenses?: Expense[];
}

export interface TopExpenseByTag {
  tag: string;
  total: number;
  expenseIds: Set<number>;
  totalWeight: number;
  totalQty: number;
  rate: number;
  maxRate: number;
  minRate: number;
  avgRate: number;
  expenses?: Expense[];
}

export interface TopExpenseByGroup {
  groupId: number;
  total: number;
  expenseIds: Set<number>;
  expenses?: Expense[];
}

export interface TopExpenseByGroupTag {
  groupTagId: number;
  total: number;
  expenseIds: Set<number>;
  expenses?: Expense[];
}

export interface ByGroup {
  groupId: number;
  expenseIds: Set<number>;
  total: number;
}

export interface ByTag {
  tag: string;
  total: number;
  totalQty: number;
  totalWeight: number;
  rate: number;
  maxRate: number;
  minRate: number;
  avgRate: number;
  expenseIds: Set<number>;
}

export interface ByDay {
  day: number;
  total: number;
  expenseIds: Set<number>;
  summary: string;
}

export interface ByGroupTags {
  groupTagId: number;
  expenseIdsForTags: Set<number>;
  tags: Map<string, { tag: string; total: number; expenseIds: Set<number> }>;
  total: number;
}

export interface ExpenseMonthly {
  expenses: Set<number>;
  monthName: string;
  month: number;
  year: number;
  total: number;
  byDay: Map<number, ByDay>;
  byTag: Map<string, ByTag>;
  byGroup: Map<number, ByGroup>;
  byGroupTags: Map<number, ByGroupTags>;
  topExpenseByDay: Array<TopExpenseByDay>;
  topExpenseByTag: Array<TopExpenseByTag>;
  topExpenseByGroup: Array<TopExpenseByGroup>;
  topExpenseByGroupTag: Array<TopExpenseByGroupTag>;
}

export interface ExpenseYearly {
  year: number;
  total: number;
  expensesByMonth: Map<number, ExpenseMonthly>;
}

export interface ExpenseByYears {
  year: Map<number, ExpenseYearly>;
}

export interface ChartDataset {
  backgroundColor: string;
  borderColor: string;
  data: number[];
  label: string;
  tension: number;
  expenseIds: Set<number>;
  expenseSummary: Map<number, Map<number, string>>;
}

export interface SplitTags {
  tag: string;
  splitValue: number;
}

export interface ExpenseExtraInfo {
  iconUrl?: string;
  receiptFileName?: string;
  reOccurringId: number;
  splitTags: SplitTags[];
}

export interface TagDetails {
  tag: string;
  expenses: Expense[];
  totalEntries: number;
  total: number;
  quantity: number;
  weight: number;
  maxRate: number;
  minRate: number;
  avgRate: number;
  firstPurchaseDate?: Date;
  lastPurchaseDate?: Date;
  mostFrequentMonth?: string;
  biggestSinglePurchase?: number;
  biggestSingleQuantity?: number;
  avgDaysBetweenPurchases?: number;
  percentOfTotalExpenses?: number;
  totalExpenses?: number;
  associatedTags?: Set<string>;
  associatedTagsDetails?: TagDetails[];
}

export interface AssociatedTagsDetails {
  associatedTagsDetails: TagDetails[] | undefined;
  associatedTags: Set<string> | undefined;
}

type AffectedExpensePeriods = Map<number, Set<number>>;

//#endregion

@Injectable({
  providedIn: 'root',
})
export class ExpenseEngineService {
  //#region properties
  private localStorageKey = 'ExpenseCache';
  // simplifying create a map for all expenses
  // id of expense is also used as map key so
  // it can be retrivable quickly since
  // its unique.
  private expenseCache = new Map<number, Expense>();
  private expenseByYears!: ExpenseByYears;
  private monthNames: string[] = new Array(12)
    .fill(0)
    .map((_, i) =>
      new Intl.DateTimeFormat('default', { month: 'long' }).format(
        new Date(new Date().getFullYear(), i, 1),
      ),
    );

  private currentYear = new Date().getFullYear();

  private groupTags = new Map<string, number[]>();
  private groups = new Map<number, iGroup>();
  private groupTag!: IGroupTags[];

  private chartDataSet: Map<number, Map<number, Partial<ChartDataset>>> =
    new Map();
  destroyed$ = new Subject<void>();
  ignoreTags!: iIgnoreTags[];

  expenseSummaryDone$ = new Subject<void>();
  groupsUsedSoFar = new Map<number, number>();
  private expenseProcessingQueue = Promise.resolve();

  //#endregion

  constructor(
    private currencyPipe: CurrencyPipe,
    private store: Store<ExpenseTrackerStoreState>,
    private spinnerService: SpinnerService,
  ) {
    this.initializeExpenseByYear(this.currentYear);

    this.store
      .select(getAllIgnoreTags)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((ignoreTags) => {
        this.ignoreTags = ignoreTags;
      });

    this.store
      .select(getAllGroups)
      .pipe(takeUntil(this.destroyed$), distinctUntilChanged())
      .subscribe((groups) => {
        if (groups && groups.length > 0) {
          groups.forEach((group) => {
            // adding group
            if (this.groups.has(group.id) == false) {
              this.groups.set(group.id, group);
            } else {
              group.isDeleted
                ? this.groups.delete(group.id)
                : this.groups.set(group.id, group);
            }
          });
        }
      });

    this.store
      .select(getAllGroupTags)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((groupTags) => {
        if (groupTags && groupTags.length > 0) {
          this.groupTag = groupTags;
          this.InitializeGroupTag();
        }
      });
  }

  //#region Initialize Engine methods

  setExpenses(expenses: Expense[]) {
    if (expenses && expenses.length > 0) {
      const expensesToProcess = [...expenses];

      this.expenseProcessingQueue = this.expenseProcessingQueue
        .then(() => this.AddExpensesAsync(expensesToProcess))
        .catch((error) => {
          console.log('error happened when generating yearly expense');
          console.log(error);
        });
    }
  }

  private InitializeGroupTag() {
    // a tag can be a part of many groups
    // adding tags as sets key so i can by
    // quickly fetch because we will use
    // expense.tagArray's tag value to look up
    this.groupTag?.forEach((gTag) =>
      gTag.tagsArray?.forEach((tag) => {
        // getting tag from Map
        const tagObj = this.groupTags.get(tag);

        // since tag group is deleted we are removing linked tags
        if (gTag.isDeleted && tagObj) {
          // removing group id from array
          this.groupTags.set(
            tag,
            tagObj.filter((groupId) => groupId != gTag.id),
          );
        } else {
          // if tag exists add group id to it
          if (tagObj) {
            tagObj.push(gTag.id!);
          } else {
            // if group tags is deleted do not set
            if (!gTag.isDeleted) {
              // if tag not exist initialize it.
              this.groupTags.set(tag, [gTag.id!]);
            }
          }
        }
      }),
    );
  }

  private initializeExpenseByYear(year: number) {
    // add year to expenseByYear Map if its not initialized
    if (!this.expenseByYears?.year?.get(year)) {
      this.expenseByYears = this.expenseByYears || { year: new Map() };
      const yearlyExpense: ExpenseYearly = {
        year,
        total: 0,
        expensesByMonth: new Map<number, ExpenseMonthly>(),
      };
      this.expenseByYears.year.set(year, yearlyExpense);
    }
  }

  private InitializeMonthlyExpensesForYear(year: number, month: number) {
    // only add month if its doesn't already exist
    if (!this.expenseByYears.year.get(year)?.expensesByMonth?.get(month)) {
      const monthlyExpense: ExpenseMonthly = {
        expenses: new Set(),
        year,
        month: month,
        monthName: this.monthNames[month - 1],
        byDay: new Map(),
        byGroup: new Map(),
        byGroupTags: new Map(),
        byTag: new Map(),
        topExpenseByDay: new Array(),
        topExpenseByTag: new Array(),
        topExpenseByGroup: new Array(),
        topExpenseByGroupTag: new Array(),
        total: 0,
      };
      this.expenseByYears.year
        .get(year)!
        .expensesByMonth.set(month, monthlyExpense);
    }
  }

  //#endregion

  //#region Cache logic

  private AddExpenses(expenses: Expense[]) {
    this.spinnerService.showSpinner();
    const affectedPeriods =
      this.AddUpdateExpensesAndGenerateSummaryFromResponseToCache(expenses);
    this.UpdateOverAllSummaries(affectedPeriods);
    this.expenseSummaryDone$.next();
    this.spinnerService.hideSpinner();
  }

  private async AddExpensesAsync(expenses: Expense[]) {
    this.spinnerService.showSpinner();
    const affectedPeriods =
      await this.AddUpdateExpensesAndGenerateSummaryFromResponseToCacheAsync(
      expenses,
    );
    await this.UpdateOverAllSummariesAsync(affectedPeriods);
    this.expenseSummaryDone$.next();
    this.spinnerService.hideSpinner();
  }

  private AddUpdateExpensesAndGenerateSummaryFromResponseToCache(
    expenses: Expense[],
  ): AffectedExpensePeriods {
    const affectedPeriods = new Map<number, Set<number>>();

    try {
      expenses?.forEach((expense) => {
        const juicedUpExpense = this.addMoreToExpenseObj(expense);
        let oldExpense = this.expenseCache.get(expense.id);
        // deep copying it to avoid any referencing
        if (oldExpense) {
          oldExpense = this.addMoreToExpenseObj(
            JSON.parse(JSON.stringify(oldExpense)),
          );
        }

        this.expenseCache.set(expense.id, juicedUpExpense);
        this.markAffectedExpensePeriod(affectedPeriods, juicedUpExpense);
        this.markAffectedExpensePeriod(affectedPeriods, oldExpense);

        // now do extra info and populate yearly, monthly expense stats
        this.parseExpenseForStats(juicedUpExpense, oldExpense);
      });
      // console.log('expense generated successfully', expenses);
      // console.log('expense stats', this.expenseByYears);
    } catch (error) {
      console.log('error happened when generating yearly expense');
      console.log(error);
    }

    return affectedPeriods;
  }

  private async AddUpdateExpensesAndGenerateSummaryFromResponseToCacheAsync(
    expenses: Expense[],
  ): Promise<AffectedExpensePeriods> {
    const affectedPeriods = new Map<number, Set<number>>();

    try {
      const batchSize = 250;

      for (let index = 0; index < expenses.length; index++) {
        const expense = expenses[index];
        const juicedUpExpense = this.addMoreToExpenseObj(expense);
        let oldExpense = this.expenseCache.get(expense.id);

        if (oldExpense) {
          oldExpense = this.addMoreToExpenseObj(
            JSON.parse(JSON.stringify(oldExpense)),
          );
        }

        this.expenseCache.set(expense.id, juicedUpExpense);
        this.markAffectedExpensePeriod(affectedPeriods, juicedUpExpense);
        this.markAffectedExpensePeriod(affectedPeriods, oldExpense);
        this.parseExpenseForStats(juicedUpExpense, oldExpense);

        if (index > 0 && index % batchSize === 0) {
          await this.waitForNextFrame();
        }
      }
    } catch (error) {
      console.log('error happened when generating yearly expense');
      console.log(error);
    }

    return affectedPeriods;
  }

  private waitForNextFrame(): Promise<void> {
    return new Promise((resolve) => {
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(() => resolve());
        return;
      }

      setTimeout(resolve, 0);
    });
  }

  private UpdateOverAllSummaries(affectedPeriods?: AffectedExpensePeriods) {
    const yearsToUpdate = affectedPeriods?.size
      ? Array.from(affectedPeriods.keys())
      : Array.from(this.expenseByYears?.year?.keys() || []);

    yearsToUpdate.forEach((yearNumber) => {
      const year = this.expenseByYears?.year?.get(yearNumber);

      if (!year) {
        return;
      }

      this.UpdateYearTotal(year);
      this.UpdateMonthlyOverAllSummaries(year, affectedPeriods?.get(yearNumber));
    });
  }

  private async UpdateOverAllSummariesAsync(
    affectedPeriods?: AffectedExpensePeriods,
  ) {
    const yearsToUpdate = affectedPeriods?.size
      ? Array.from(affectedPeriods.keys())
      : Array.from(this.expenseByYears?.year?.keys() || []);

    for (const yearNumber of yearsToUpdate) {
      const year = this.expenseByYears?.year?.get(yearNumber);

      if (!year) {
        continue;
      }

      this.UpdateYearTotal(year);
      await this.UpdateMonthlyOverAllSummariesAsync(
        year,
        affectedPeriods?.get(yearNumber),
      );
    }
  }

  private UpdateMonthlyOverAllSummaries(
    year: ExpenseYearly,
    monthsToUpdate?: Set<number>,
  ) {
    year.expensesByMonth.forEach((monthlyExpense, monthNumber) => {
      if (monthsToUpdate && !monthsToUpdate.has(monthNumber)) {
        return;
      }

      this.UpdateMonthlySummary(monthlyExpense);
    });
  }

  private async UpdateMonthlyOverAllSummariesAsync(
    year: ExpenseYearly,
    monthsToUpdate?: Set<number>,
  ) {
    for (const [monthNumber, monthlyExpense] of year.expensesByMonth.entries()) {
      if (monthsToUpdate && !monthsToUpdate.has(monthNumber)) {
        continue;
      }

      this.UpdateMonthlySummary(monthlyExpense);
      await this.waitForNextFrame();
    }
  }

  private UpdateMonthlySummary(monthlyExpense: ExpenseMonthly) {
    monthlyExpense.topExpenseByDay = Array.from(monthlyExpense.byDay)
      .map((item) => item[1])
      .sort((a, b) => b.total - a.total)
      .map((item) => {
        return {
          day: item.day,
          total: item.total,
          expenseIds: item.expenseIds,
        };
      });

    monthlyExpense.topExpenseByGroup = Array.from(monthlyExpense.byGroup)
      .map((item) => item[1])
      .sort((a, b) => b.total - a.total)
      .map((item) => {
        return {
          groupId: item.groupId,
          total: item.total,
          expenseIds: item.expenseIds,
        };
      });

    monthlyExpense.topExpenseByGroupTag = Array.from(monthlyExpense.byGroupTags)
      .map((item) => item[1])
      .sort((a, b) => b.total - a.total)
      .map((item) => {
        return {
          groupTagId: item.groupTagId,
          total: item.total,
          expenseIds: item.expenseIdsForTags,
        };
      });

    monthlyExpense.topExpenseByTag = Array.from(monthlyExpense.byTag)
      .map((item) => item[1])
      .sort((a, b) => b.total - a.total)
      .map((item) => {
        return {
          tag: item.tag,
          total: item.total,
          totalQty: item.totalQty,
          totalWeight: item.totalWeight,
          expenseIds: item.expenseIds,
          rate: item.rate,
          maxRate: item.maxRate,
          minRate: item.minRate,
          avgRate: item.avgRate,
        };
      });
  }

  private markAffectedExpensePeriod(
    affectedPeriods: AffectedExpensePeriods,
    expense?: Expense,
  ) {
    if (!expense || !expense.year || !expense.month) {
      return;
    }

    const months = affectedPeriods.get(expense.year) || new Set<number>();
    months.add(expense.month);
    affectedPeriods.set(expense.year, months);
  }

  private UpdateYearTotal(year: ExpenseYearly) {
    year.total = 0;
    year.expensesByMonth.forEach(
      (monthlyExpense) => (year.total += monthlyExpense.total),
    );
  }

  //#endregion

  //#region helper methods

  private addMoreToExpenseObj(expense: Expense): Expense {
    // converting tracker value to number
    expense.trackerValue = parseFloat(expense.trackerValue?.toString());

    // set expense dated object
    if (expense.dated) {
      expense.datedObj = new Date(expense.dated);
    }

    // add extra info object to root of expense object
    if (expense.extraInfo) {
      const extraInfo = expense.extraInfo;
      expense = { ...expense, ...extraInfo };
    }

    // now do some cleaning in split-tags,
    // only keep those tags whose amount
    // is different from expense amount
    if (expense.splitTags) {
      expense.splitTags
        .filter((tag) => +tag.splitValue !== expense.trackerValue)
        .forEach((splitTag) => expense.splitTags);
    }

    // work on special tags
    // convert weight and add to object
    expense.tagsArray.forEach((tag) => {
      expense.weight = this.getWeightFromTag(tag);
      expense.quantity = this.getQuantityFromTag(tag);
      expense.rate = this.getRateFromTag(tag);
    });

    return expense;
  }

  getWeightFromTag(tag: string) {
    let weightInLbs = 0;
    if (tag.indexOf('weight:') >= 0) {
      tag = tag.toLowerCase().replace('weight:', '');

      if (tag.indexOf('kg') >= 0) {
        weightInLbs =
          parseFloat(tag.replace('kg', '').replace('kgs', '')) * 2.2;
      } else {
        weightInLbs = parseFloat(tag.replace('lb', '').replace('lbs', ''));
      }
    }
    return weightInLbs;
  }

  getQuantityFromTag = (tag: string) => {
    if (tag.indexOf('qty:') >= 0) {
      return this.getTagValue(tag);
    } else return 1;
  };

  getRateFromTag = (tag: string) => {
    if (tag.indexOf('rate:') >= 0 || tag.indexOf('rates:') >= 0) {
      return this.getTagValue(tag, true);
    } else return 0;
  };

  private getTagValue(tag: string, returnZero = false) {
    let value = parseFloat(tag.split(':')[1]);
    if (!value) {
      value = returnZero ? 0 : 1;
    }
    return value;
  }

  private getExpenseValueByTag(tag: string, expense: Expense) {
    let expenseValue = +expense.trackerValue;
    const splitTag = expense.splitTags?.find((stag) => stag.tag == tag);
    return splitTag ? +splitTag.splitValue : expenseValue;
  }

  //#endregion

  //#region Expense info extraction

  private parseExpenseForStats(expense: Expense, oldEntry?: Expense) {
    const expenseYear = expense.year;
    const expenseMonth = expense.month;

    // initialize expense year
    this.initializeExpenseByYear(expenseYear);

    // initialize expense month for that year
    this.InitializeMonthlyExpensesForYear(expenseYear, expenseMonth);

    const monthObj = this.expenseByYears.year
      .get(expenseYear)!
      .expensesByMonth.get(expenseMonth)!;

    // all summaries done here
    this.updateMonthSummary(monthObj, expense, oldEntry);
    this.addToByDaySummary(monthObj, expense, oldEntry);
    this.addToByGroupSummary(monthObj, expense, oldEntry);
    this.addToByGroupTagsSummary(monthObj, expense, oldEntry);
    this.addToByTagSummary(monthObj, expense, oldEntry);

    // set chart data
    this.setChartData(monthObj, expense, oldEntry);

    // set group used so far attribute
    this.updateGroupUsedSoFar(expense);

    // adding expense ids to month object
    monthObj.expenses.add(expense.id);
  }

  private updateGroupUsedSoFar(expense: Expense) {
    if (expense.groupId) {
      const group = this.groupsUsedSoFar.get(expense.groupId);

      let usedSoFar = group || 0;
      usedSoFar === 0 ? (usedSoFar = 1) : usedSoFar++;

      this.groupsUsedSoFar.set(expense.groupId, usedSoFar);
    }
  }

  private updateMonthSummary(
    monthObj: ExpenseMonthly,
    expense: Expense,
    oldEntry?: Expense,
  ) {
    // subtracting old value
    if (oldEntry) {
      monthObj.total = monthObj.total - oldEntry.trackerValue;
    }

    monthObj.total += expense.trackerValue;
  }

  private addToByDaySummary(
    monthObj: ExpenseMonthly,
    expense: Expense,
    oldEntry?: Expense,
  ) {
    const day = expense.day;

    // getting day from summary
    // if not found initialize day
    let daySummary = monthObj.byDay.get(day)!;
    if (!daySummary) {
      monthObj.byDay.set(day, {
        day: day,
        expenseIds: new Set(),
        total: 0,
        summary: '',
      });
      daySummary = monthObj.byDay.get(day)!;
    }

    // if old entry is there, remove that entry's stats
    // from object so newly can be added
    if (oldEntry) {
      daySummary.expenseIds.delete(expense.id);
      daySummary.total -= oldEntry.trackerValue;
      daySummary.summary.replace(
        `You've spend ${this.currencyPipe.transform(
          expense.trackerValue,
        )} at ${expense.tagsArray.join('<br>').substring(0, 30)}...`,
        '',
      );
    }

    // if expense id is found for that day
    // then its a coming for an update
    // if not found, add it
    if (daySummary.expenseIds.has(expense.id) == false) {
      daySummary.expenseIds.add(expense.id);
      daySummary.total += expense.trackerValue;

      daySummary.summary += `You've spend ${this.currencyPipe.transform(
        expense.trackerValue,
      )} at ${expense.tagsArray.join('<br>').substring(0, 30)}...`;
    }
  }

  private addToByTagSummary(
    monthObj: ExpenseMonthly,
    expense: Expense,
    oldEntry?: Expense,
  ) {
    expense.tagsArray.forEach((tag) => {
      let tagSummary = monthObj.byTag.get(tag)!;
      if (!tagSummary) {
        monthObj.byTag.set(tag, {
          expenseIds: new Set(),
          tag: tag,
          total: 0,
          totalQty: 0,
          totalWeight: 0,
          rate: 0,
          maxRate: 0,
          minRate: 0,
          avgRate: 0,
        });
        tagSummary = monthObj.byTag.get(tag)!;
      }

      // removing old entry and its stats
      if (oldEntry) {
        tagSummary.expenseIds.delete(expense.id);
        tagSummary.total -= this.getExpenseValueByTag(tag, expense);
        tagSummary.totalQty -= oldEntry.quantity!;
        tagSummary.totalWeight -= oldEntry.weight!;
        tagSummary.rate = 0;
        tagSummary.maxRate = 0;
        tagSummary.minRate = 0;
      }

      if (tagSummary.expenseIds.has(expense.id) == false) {
        tagSummary.expenseIds.add(expense.id);
        tagSummary.total += this.getExpenseValueByTag(tag, expense);
        tagSummary.totalQty += expense.quantity!;
        tagSummary.totalWeight += expense.weight!;
        tagSummary.rate = expense.rate!;
        tagSummary.maxRate = Math.max(
          tagSummary.maxRate,
          expense.rate ? expense.rate : 0,
        );
        tagSummary.minRate = Math.min(
          tagSummary.minRate,
          expense.rate ? expense.rate : 0,
        );
      }
    });
  }

  private addToByGroupTagsSummary(
    monthObj: ExpenseMonthly,
    expense: Expense,
    oldEntry?: Expense,
  ) {
    expense.tagsArray.forEach((tag) => {
      const tagGroupIdsForTag = this.groupTags.get(tag);

      // a tag i.e. "nadia" can be in multiple groups
      // so we get id of the group, fetch it from byGroupTags
      // find the tag in expense and add it
      tagGroupIdsForTag?.forEach((tagIG) => {
        // getting groupTag id so we can push
        // information about expense
        // if not found initialize array
        let groupExpense = monthObj.byGroupTags.get(tagIG)!;
        if (!groupExpense) {
          monthObj.byGroupTags.set(tagIG, {
            groupTagId: tagIG,
            expenseIdsForTags: new Set(),
            tags: new Map(),
            total: 0,
          });
          groupExpense = monthObj.byGroupTags.get(tagIG)!;
        }

        // now getting tag from expense group
        // each group has tags
        // each tag is maintaining how much it spend

        // starting groupTag's tag summary logic
        let groupExpenseTag = groupExpense?.tags.get(tag)!;
        if (!groupExpenseTag) {
          groupExpense.tags.set(tag, {
            expenseIds: new Set<number>(),
            total: 0,
            tag: '',
          });
          groupExpenseTag = groupExpense.tags.get(tag)!;
        }

        if (oldEntry) {
          groupExpenseTag.expenseIds.delete(expense.id);
          groupExpenseTag.total -= this.getExpenseValueByTag(tag, oldEntry);
          groupExpense.total -= this.getExpenseValueByTag(tag, expense);
        }

        // just in case user added same tag twice it will not add it
        if (groupExpenseTag.expenseIds.has(expense.id) == false) {
          groupExpenseTag.expenseIds.add(expense.id);
          groupExpenseTag.total += this.getExpenseValueByTag(tag, expense);
          groupExpenseTag.tag = tag;
        }
        // ending groupTag's tag summary logic

        // now updating final groupTag obj
        // adding expense to groupTag
        groupExpense.expenseIdsForTags.add(expense.id);
        // mainting group total
        groupExpense.total += this.getExpenseValueByTag(tag, expense);
      });
    });
  }

  private addToByGroupSummary(
    monthObj: ExpenseMonthly,
    expense: Expense,
    oldEntry?: Expense,
  ) {
    if (expense.groupId) {
      let byGroup = monthObj.byGroup.get(expense.groupId)!;
      if (!byGroup) {
        monthObj.byGroup.set(expense.groupId, {
          expenseIds: new Set(),
          groupId: expense.groupId,
          total: 0,
        });
        byGroup = monthObj.byGroup.get(expense.groupId)!;
      }

      if (oldEntry) {
        byGroup.expenseIds.delete(expense.id);
        byGroup.total -= oldEntry.trackerValue;
      }

      const hasExpense = byGroup.expenseIds.has(expense.id);
      if (!hasExpense) {
        byGroup.expenseIds.add(expense.id);
        byGroup.total += expense.trackerValue;
        byGroup.groupId = expense.groupId;
      }
    }
  }

  private setChartData(
    monthObj: ExpenseMonthly,
    expense: Expense,
    oldEntry?: Expense,
  ) {
    const day = expense.day;
    const filterTags = this.getChartFiltersFromLocalStorage();

    let yearSet = this.chartDataSet.get(monthObj.year)!;
    if (!yearSet) {
      this.chartDataSet.set(monthObj.year, new Map());
      yearSet = this.chartDataSet.get(monthObj.year)!;
    }

    let monthlyChartData = yearSet.get(monthObj.month)!;
    if (!monthlyChartData) {
      yearSet.set(monthObj.month, {
        label: monthObj.monthName,
        backgroundColor: ChartColors[monthObj.month],
        borderColor: ChartColors[monthObj.month],
        data: [],
        expenseIds: new Set(),
        tension: 0.2,
        expenseSummary: new Map(),
      });
      monthlyChartData = yearSet.get(monthObj.month)!;
    }

    // removing old entry
    if (oldEntry) {
      const oldEntryDay = oldEntry.day;
      monthlyChartData.expenseIds!.delete(oldEntry.id);
      // removing summary generated for that expense for that day
      monthlyChartData.expenseSummary!.get(oldEntryDay)?.delete(oldEntry.id);
    }

    const tagFound =
      expense.tagsArray.filter((tag) => filterTags.includes(tag)).length > 0;

    // if tag found and its in a set, delete it
    if (tagFound) {
      if (monthlyChartData.expenseIds!.has(expense.id)) {
        monthlyChartData.expenseIds!.delete(expense.id);
        // removing summary generated for that expense for that day
        monthlyChartData.expenseSummary!.get(day)?.delete(expense.id);
      }
    } else {
      // if tag is not found.. add expense id to an array
      monthlyChartData.expenseIds!.add(expense.id);

      // generating fucking summary for chart hover tooltip
      let expenseSummary = monthlyChartData.expenseSummary!.get(day);
      if (!expenseSummary) {
        monthlyChartData.expenseSummary!.set(day, new Map());
        expenseSummary = monthlyChartData.expenseSummary!.get(day);
      }
      expenseSummary!.set(
        expense.id,
        `You spent ${(+expense.trackerValue).toFixed(2)} at ${expense.tagsArray
          .join(', ')
          .substring(0, 30)}...`,
      );
    }
  }

  getChartFiltersFromLocalStorage() {
    try {
      const key = `${localStorage.getItem('userId')}-yearly-chart`;
      const filterTags = localStorage.getItem(key);
      return filterTags ? JSON.parse(filterTags) : [];
    } catch (error) {
      return undefined;
    }
  }

  setChartFiltersFromLocalStorage(items: any[]) {
    try {
      const key = `${localStorage.getItem('userId')}-yearly-chart`;
      localStorage.setItem(key, JSON.stringify(items));
    } catch (error) {}
  }

  getTagDetails(tag: string, parentTag?: string): TagDetails {
    let qty: number = 0;
    const tagDetailsByExpenses: Set<Expense> = new Set();
    let weight: number = 0;
    let rate: number[] = [];
    let total: number = 0;
    let allDates: Date[] = [];
    let biggestSinglePurchase = 0;
    let biggestSingleQuantity = 0;
    let monthCount: Record<string, number> = {};
    let totalExpenses = 0;

    // Calculate total expenses for percentage
    this.expenseByYears.year.forEach((year) => {
      year.expensesByMonth.forEach((monthObj) => {
        totalExpenses += monthObj.total;
      });
    });

    this.expenseByYears.year.forEach((year) => {
      year.expensesByMonth.forEach((monthObj) => {
        const tagSummary = monthObj.byTag.get(tag);
        if (tagSummary) {
          let mappedExpenses = this.MapExpensesToIds(tagSummary.expenseIds);

          if (parentTag) {
            mappedExpenses = mappedExpenses.filter((expense) =>
              expense.tagsArray.includes(parentTag),
            );
          }

          if (mappedExpenses.length > 0) {
            mappedExpenses.forEach((expense) => {
              tagDetailsByExpenses.add(expense);
              // Track biggest single purchase
              const expenseValue = this.getExpenseValueByTag(tag, expense);

              if (expenseValue > biggestSinglePurchase) {
                biggestSinglePurchase = expenseValue;
              }

              if (
                expense.quantity &&
                expense.quantity > biggestSingleQuantity
              ) {
                biggestSingleQuantity = expense.quantity;
              }

              // Track purchase dates
              if (expense.datedObj) {
                allDates.push(expense.datedObj);
                // Track month frequency
                const month = expense.datedObj.toLocaleString('default', {
                  month: 'long',
                  year: 'numeric',
                });
                monthCount[month] = (monthCount[month] || 0) + 1;
              }

              qty += expense.quantity || 0;
              weight += expense.weight || 0;
              rate.push(expense.rate || 0);
              total += this.getExpenseValueByTag(tag, expense);
            });
          }
        }
      });
    });

    rate = rate.filter((rate) => rate > 0);
    allDates = allDates.sort((a, b) => a.getTime() - b.getTime());
    let firstPurchaseDate = allDates[0] || undefined;
    let lastPurchaseDate = allDates[allDates.length - 1] || undefined;

    // Most frequent purchase month
    let mostFrequentMonth = undefined;
    let maxMonthCount = 0;
    for (const [month, count] of Object.entries(monthCount)) {
      if (count > maxMonthCount) {
        maxMonthCount = count;
        mostFrequentMonth = month;
      }
    }

    // Average days between purchases
    let avgDaysBetweenPurchases = undefined;
    if (allDates.length > 1) {
      let totalDays = 0;
      for (let i = 1; i < allDates.length; i++) {
        totalDays +=
          (allDates[i].getTime() - allDates[i - 1].getTime()) /
          (1000 * 60 * 60 * 24);
      }
      avgDaysBetweenPurchases = totalDays / (allDates.length - 1);
    }

    // Percentage of total expenses
    let percentOfTotalExpenses =
      totalExpenses > 0 ? (total / totalExpenses) * 100 : 0;

    return {
      tag: tag,
      expenses: Array.from(tagDetailsByExpenses),
      totalEntries: tagDetailsByExpenses.size,
      total: total,
      quantity: qty,
      weight: weight,
      maxRate: rate.length ? Math.max(...rate) : 0,
      minRate: rate.length ? Math.min(...rate) : 0,
      avgRate: rate.length ? rate.reduce((a, b) => a + b, 0) / rate.length : 0,
      firstPurchaseDate,
      lastPurchaseDate,
      mostFrequentMonth,
      biggestSinglePurchase,
      biggestSingleQuantity,
      avgDaysBetweenPurchases,
      percentOfTotalExpenses,
      totalExpenses,
    };
  }

  //#endregion

  //#region getter and setters

  private getChartDataByYearWithFiltersApplied(year: number) {
    const yearObj = this.expenseByYears.year.get(year)!;

    yearObj.expensesByMonth.forEach((monthObj) => {
      this.MapExpensesToIds(monthObj.expenses)
        .filter((expenses) => expenses.year === year)
        .forEach((expense) => {
          // set chart data
          this.setChartData(monthObj, expense);
        });
    });

    return this.chartDataSet.get(year);
  }

  getYearData(year: number) {
    return this.expenseByYears.year.get(year);
  }

  MapExpensesToIds(setObj: Set<number>, filterTags?: string[]) {
    return Array.from(setObj, (expenseId, _) => {
      const expense = this.expenseCache.get(expenseId)!;
      if (filterTags) {
        const tagFound =
          expense.tagsArray.filter((tag) => filterTags.includes(tag)).length >
          0;

        if (tagFound) return null;
      }
      return expense;
    }).filter((item) => item != null);
  }

  getChartDataByYear(year: number, newFiltersApplied = false) {
    if (newFiltersApplied) {
      return this.getChartDataByYearWithFiltersApplied(year);
    }

    const data = this.chartDataSet.get(year);
    if (data) {
      return this.chartDataSet.get(year);
    }

    return this.getChartDataByYearWithFiltersApplied(year);
  }

  getMonthIndex(month: string) {
    const name = this.monthNames.filter((item) => month == item);
    return name.length > 0 ? name[0] : -1;
  }

  getMonthNameByIndex(index: number) {
    return this.monthNames[index] || '';
  }

  getTopExpenseCategoriesForYear(
    year: number,
    topN: number = 10,
  ): TopExpenseByTag[] {
    const yearData = structuredClone(this.getYearData(year));
    const tagMap = new Map<string, TopExpenseByTag>();
    const ignoreTags = this.ignoreTags
      .filter((tag) => tag.yearly === 1)
      .map((tag) => tag.tag);

    if (yearData) {
      yearData.expensesByMonth.forEach((monthData) => {
        monthData.byTag.forEach((tagData: ByTag) => {
          if (!ignoreTags.includes(tagData.tag)) {
            if (!tagMap.has(tagData.tag)) {
              tagMap.set(tagData.tag, {
                tag: tagData.tag,
                total: tagData.total,
                totalQty: tagData.totalQty,
                totalWeight: tagData.totalWeight,
                expenseIds: tagData.expenseIds,
                rate: tagData.rate,
                maxRate: tagData.maxRate,
                minRate: tagData.minRate,
                avgRate: tagData.avgRate,
              });
            } else {
              const existingTag = tagMap.get(tagData.tag)!;
              existingTag.total += tagData.total;
              existingTag.totalQty += tagData.totalQty;
              existingTag.totalWeight += tagData.totalWeight;
              existingTag.expenseIds = new Set([
                ...existingTag.expenseIds,
                ...tagData.expenseIds,
              ]);
              existingTag.rate = tagData.rate;
              existingTag.maxRate = Math.max(
                existingTag.maxRate,
                tagData.maxRate,
              );
              existingTag.minRate = Math.min(
                existingTag.minRate,
                tagData.minRate,
              );
              existingTag.avgRate = (existingTag.avgRate + tagData.avgRate) / 2; // Average of averages
            }
          }
        });
      });
    }

    return Array.from(tagMap.values())
      .sort((a, b) => b.total - a.total)
      .slice(0, topN);
  }

  //#endregion
}
